/**
 * Shared members panel (spaces + rooms)
 *
 * Presentational roster UI extracted from the original SpaceMembers component
 * (MP-217): member list with role dropdowns and remove buttons, a roles-help
 * disclosure, and the add-member modal with the user-search typeahead. The
 * space and room wrappers (`space-members.tsx`, `room-members.tsx`) bind it to
 * their respective tRPC procedures via the async `onAdd`/`onChangeRole`/
 * `onRemove` callbacks — the panel owns all transient UI state (pending
 * actions, error surfacing, modal visibility).
 *
 * Copy that differs between the two contexts ("this space" vs "this room",
 * role explanations) resolves through `copyPrefix`; invariant widget copy
 * (search box, role names, buttons) stays under the shared `members.*` keys.
 */

import {
	ChevronDown,
	Crown,
	Edit2,
	Eye,
	Info,
	Search,
	Shield,
	Trash2,
	UserPlus,
	Users,
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc/client'
import { Alert, Button, IconButton, Modal, Select, Spinner, TextField } from '../ui'

export type MemberRole = 'VIEWER' | 'EDITOR' | 'ADMIN'

export interface PanelMember {
	id: string
	user: { id: string; name: string | null; email: string }
	role: string
	/**
	 * Space rosters include a synthetic owner row (Crown badge, no role
	 * dropdown or remove button — UAT F1). Rooms have no owner of their own,
	 * so the room roster never sets it.
	 */
	isOwner?: boolean
}

export interface MembersPanelProps {
	/**
	 * i18n prefix for context-specific copy: 'members' (space) or
	 * 'rooms.members' (room). Both prefixes define the same key set: title,
	 * addTitle, empty, confirmRemove, roleDesc.*, rolesHelp.{assignedBy,
	 * viewer, editor, admin}.
	 */
	copyPrefix: 'members' | 'rooms.members'
	members: readonly PanelMember[] | undefined
	isLoading: boolean
	/** Bound mutations — must reject with an Error whose message is user-facing. */
	onAdd: (userId: string, role: MemberRole) => Promise<unknown>
	onChangeRole: (userId: string, role: MemberRole) => Promise<unknown>
	onRemove: (userId: string) => Promise<unknown>
	/** Optional extra hint rendered inside the add-member modal (e.g. the
	 * room panel's "members also need space access" note). */
	addHint?: ReactNode
}

const ROLE_CONFIG: Record<
	MemberRole,
	{ icon: typeof Shield; colorClass: string; bgClass: string; labelKey: string }
> = {
	ADMIN: {
		icon: Shield,
		colorClass: 'text-coral-700',
		bgClass: 'bg-error/10',
		labelKey: 'members.roles.admin',
	},
	EDITOR: {
		icon: Edit2,
		colorClass: 'text-coral-700',
		bgClass: 'bg-accent/10',
		labelKey: 'members.roles.editor',
	},
	VIEWER: {
		icon: Eye,
		colorClass: 'text-base-content/60',
		bgClass: 'bg-base-200',
		labelKey: 'members.roles.viewer',
	},
}

function errorMessage(err: unknown): string {
	return err instanceof Error ? err.message : String(err)
}

export function MembersPanel({
	copyPrefix,
	members,
	isLoading,
	onAdd,
	onChangeRole,
	onRemove,
	addHint,
}: MembersPanelProps) {
	const { t } = useTranslation()
	const [showAddMember, setShowAddMember] = useState(false)
	const [actionError, setActionError] = useState<string | null>(null)
	// Coarse pending flag mirroring the pre-extraction behavior: a role change
	// disables every role dropdown, a removal disables every remove button.
	const [pendingAction, setPendingAction] = useState<'role' | 'remove' | null>(null)

	const runRosterAction = async (action: 'role' | 'remove', run: () => Promise<unknown>) => {
		setActionError(null)
		setPendingAction(action)
		try {
			await run()
		} catch (err) {
			setActionError(errorMessage(err))
		} finally {
			setPendingAction(null)
		}
	}

	const handleRemove = (userId: string, userName: string) => {
		if (!window.confirm(t(`${copyPrefix}.confirmRemove`, { name: userName }))) return
		void runRosterAction('remove', () => onRemove(userId))
	}

	const handleRoleChange = (userId: string, role: MemberRole) => {
		void runRosterAction('role', () => onChangeRole(userId, role))
	}

	return (
		<div className="space-y-4">
			<div className="flex justify-between items-center">
				<h2 className="text-lg font-semibold flex items-center gap-2">
					<Users size={20} aria-hidden />
					{t(`${copyPrefix}.title`)}
				</h2>
				<Button onClick={() => setShowAddMember(true)} leadingIcon={<UserPlus size={16} />}>
					{t('members.add')}
				</Button>
			</div>

			{/*
			 * Role explanation (MP-184/MP-185). Native <details>/<summary> so the
			 * disclosure is keyboard-operable and its expanded/collapsed state is
			 * announced by NVDA/VoiceOver — unlike title-attribute tooltips, which
			 * screen readers never reach. Rendered open so first-time managers see
			 * it without interaction; the constant `open` prop means React never
			 * rewrites the attribute on re-renders, so a user's collapse sticks.
			 */}
			<details open className="card-refined group">
				<summary className="flex cursor-pointer list-none items-center gap-2 p-4 text-sm font-medium [&::-webkit-details-marker]:hidden">
					<Info size={16} className="text-primary" aria-hidden />
					{t('members.rolesHelp.summary')}
					<ChevronDown
						size={16}
						className="ml-auto text-base-content/50 transition-transform group-open:rotate-180"
						aria-hidden
					/>
				</summary>
				<div className="space-y-3 px-4 pb-4 text-sm text-base-content/70">
					<p>{t(`${copyPrefix}.rolesHelp.assignedBy`)}</p>
					<dl className="space-y-2">
						{(['VIEWER', 'EDITOR', 'ADMIN'] as const).map((role) => {
							const { icon: RoleIcon, colorClass, labelKey } = ROLE_CONFIG[role]
							return (
								<div key={role}>
									<dt className="flex items-center gap-1.5 font-medium text-base-content">
										<RoleIcon size={14} className={colorClass} aria-hidden />
										{t(labelKey)}
									</dt>
									<dd className="mt-0.5">{t(`${copyPrefix}.rolesHelp.${role.toLowerCase()}`)}</dd>
								</div>
							)
						})}
					</dl>
				</div>
			</details>

			{actionError && <Alert tone="error">{actionError}</Alert>}

			{isLoading && (
				<div className="card-refined p-8 flex justify-center text-primary">
					<Spinner size="lg" />
				</div>
			)}

			{!isLoading && (!members || members.length === 0) && (
				<div className="card-refined p-8 text-center">
					<Users size={32} className="mx-auto text-base-content/30 mb-2" aria-hidden />
					<p className="text-base-content/60 text-sm">{t(`${copyPrefix}.empty`)}</p>
				</div>
			)}

			{members && members.length > 0 && (
				<div className="space-y-2">
					{members.map((member) => {
						return (
							<div key={member.id} className="card-refined p-4 flex items-center gap-4">
								<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<span className="text-sm font-semibold text-primary">
										{(member.user.name ?? member.user.email).charAt(0).toUpperCase()}
									</span>
								</div>
								<div className="flex-1 min-w-0">
									<p className="font-medium truncate">{member.user.name ?? member.user.email}</p>
									<p className="text-xs text-base-content/50 truncate">{member.user.email}</p>
								</div>
								{member.isOwner ? (
									// UAT F1: the owner is a synthetic roster row — their rights come
									// from Space.ownerId, so there is no role to change or row to remove.
									<span className="flex items-center gap-1.5 px-2 py-1 text-sm font-medium text-coral-700">
										<Crown size={14} aria-hidden />
										{t('members.roles.owner')}
									</span>
								) : (
									<>
										<Select
											value={member.role}
											onChange={(e) =>
												handleRoleChange(member.user.id, e.target.value as MemberRole)
											}
											className="text-sm py-1 pl-2 w-auto"
											block={false}
											disabled={pendingAction === 'role'}
											aria-label={t('members.changeRoleFor', {
												name: member.user.name ?? member.user.email,
											})}
										>
											<option value="VIEWER">{t('members.roles.viewer')}</option>
											<option value="EDITOR">{t('members.roles.editor')}</option>
											<option value="ADMIN">{t('members.roles.admin')}</option>
										</Select>
										<IconButton
											variant="danger"
											size="sm"
											onClick={() =>
												handleRemove(member.user.id, member.user.name ?? member.user.email)
											}
											disabled={pendingAction === 'remove'}
											loading={pendingAction === 'remove'}
											aria-label={t('members.removeFor', {
												name: member.user.name ?? member.user.email,
											})}
										>
											<Trash2 />
										</IconButton>
									</>
								)}
							</div>
						)
					})}
				</div>
			)}

			{showAddMember && (
				<AddMemberModal
					open={showAddMember}
					copyPrefix={copyPrefix}
					onAdd={onAdd}
					addHint={addHint}
					onClose={() => setShowAddMember(false)}
				/>
			)}
		</div>
	)
}

function AddMemberModal({
	open,
	copyPrefix,
	onAdd,
	addHint,
	onClose,
}: {
	open: boolean
	copyPrefix: MembersPanelProps['copyPrefix']
	onAdd: MembersPanelProps['onAdd']
	addHint?: ReactNode
	onClose: () => void
}) {
	const { t } = useTranslation()
	const [query, setQuery] = useState('')
	const [debouncedQuery, setDebouncedQuery] = useState('')
	// Search results include the user's email (UAT F3) so same-name users
	// can be told apart; email stays nullable for defensive rendering.
	const [selectedUser, setSelectedUser] = useState<{
		id: string
		name: string | null
		email: string | null
	} | null>(null)
	const [role, setRole] = useState<MemberRole>('EDITOR')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Debounce search input — re-runs whenever `query` changes
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300)
		return () => clearTimeout(timer)
	}, [query])

	const handleQueryChange = (value: string) => {
		setQuery(value)
		setSelectedUser(null)
	}

	const {
		data: results,
		isLoading: searching,
		error: searchError,
	} = trpc.users.search.useQuery(
		{ query: debouncedQuery },
		{ enabled: debouncedQuery.length >= 3, retry: false },
	)

	const handleAdd = async () => {
		if (!selectedUser) return
		setSubmitting(true)
		setError(null)
		try {
			await onAdd(selectedUser.id, role)
			onClose()
		} catch (err) {
			setError(errorMessage(err))
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={t(`${copyPrefix}.addTitle`)}
			size="md"
			closeLabel={t('common.close')}
		>
			<TextField
				value={query}
				onChange={(e) => handleQueryChange(e.target.value)}
				placeholder={t('members.searchPlaceholder')}
				aria-label={t('members.searchLabel')}
				leadingIcon={<Search size={16} />}
				autoFocus
				className="mb-3"
			/>

			{/* Scrollable results list */}
			<div className="max-h-60 overflow-y-auto rounded-xl border border-base-300 mb-4">
				{searching && (
					<div className="flex justify-center py-6 text-primary">
						<Spinner size="md" />
					</div>
				)}

				{/* A failed search must not masquerade as "no users found" — a manager
				  who just lost authorization (or hit the rate limit) needs the real
				  reason, not a lie about the directory being empty. */}
				{!searching && searchError && (
					<div className="py-6 px-4 text-center text-sm text-error" role="alert">
						{searchError.message}
					</div>
				)}

				{!searching &&
					!searchError &&
					debouncedQuery.length >= 3 &&
					(!results || results.length === 0) && (
						<div className="py-6 text-center text-sm text-base-content/50">
							{t('members.notFound')}
						</div>
					)}

				{!searching && debouncedQuery.length < 3 && (
					<div className="py-6 text-center text-sm text-base-content/40">
						{t('members.searchHint')}
					</div>
				)}

				{results &&
					results.length > 0 &&
					results.map((user) => (
						<button
							key={user.id}
							type="button"
							onClick={() => setSelectedUser(user)}
							aria-pressed={selectedUser?.id === user.id}
							className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-base-200 last:border-0 transition-colors ${
								selectedUser?.id === user.id ? 'bg-primary/10' : 'hover:bg-base-200'
							}`}
						>
							<div
								className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
									selectedUser?.id === user.id ? 'bg-primary/20' : 'bg-base-200'
								}`}
							>
								<span
									className={`text-sm font-semibold ${
										selectedUser?.id === user.id ? 'text-primary' : 'text-base-content/50'
									}`}
								>
									{(user.name ?? user.email ?? '?').charAt(0).toUpperCase()}
								</span>
							</div>
							<div className="min-w-0 flex-1">
								<p className="font-medium text-sm truncate">
									{user.name ?? user.email ?? t('members.unnamed')}
								</p>
								{user.email && (
									<p className="text-xs text-base-content/50 truncate">{user.email}</p>
								)}
							</div>
							{selectedUser?.id === user.id && (
								<div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
							)}
						</button>
					))}
			</div>

			{/* Role selection + add button (shown when user selected) */}
			{selectedUser && (
				<div className="space-y-3">
					<div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl p-3">
						<div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
							<span className="text-xs font-semibold text-primary">
								{(selectedUser.name ?? selectedUser.email ?? '?').charAt(0).toUpperCase()}
							</span>
						</div>
						<div className="min-w-0 flex-1">
							<p className="font-medium text-sm truncate">
								{selectedUser.name ?? selectedUser.email ?? t('members.unnamed')}
							</p>
							{selectedUser.email && (
								<p className="text-xs text-base-content/50 truncate">{selectedUser.email}</p>
							)}
						</div>
					</div>

					<Select
						label={t('members.role')}
						value={role}
						onChange={(e) => setRole(e.target.value as MemberRole)}
					>
						<option value="VIEWER">
							{t('members.roles.viewer')} — {t(`${copyPrefix}.roleDesc.viewer`)}
						</option>
						<option value="EDITOR">
							{t('members.roles.editor')} — {t(`${copyPrefix}.roleDesc.editor`)}
						</option>
						<option value="ADMIN">
							{t('members.roles.admin')} — {t(`${copyPrefix}.roleDesc.admin`)}
						</option>
					</Select>

					{addHint}

					{error && <Alert tone="error">{error}</Alert>}

					<Button
						onClick={handleAdd}
						loading={submitting}
						block
						leadingIcon={<UserPlus size={16} />}
					>
						{t('members.addButton')}
					</Button>
				</div>
			)}
		</Modal>
	)
}
